import { z } from 'zod';
import { AUTOMATION_ACTIONS, AUTOMATION_CONDITION_FIELDS, AUTOMATION_OPERATORS, AUTOMATION_SCOPES, AUTOMATION_TRIGGERS, automationCompatibilityError, automationScopeOfTrigger, MAX_EMOJI_NAME_LENGTH, MAX_INCOMING_WEBHOOK_ATTACHMENTS, MAX_MESSAGE_LENGTH, MEETING_RECORDING_MAX_SECONDS, MAX_WEBHOOK_NAME_LENGTH, OUTGOING_WEBHOOK_EVENTS, MAX_REMINDER_NOTE_LENGTH, MAX_REMINDER_TITLE_LENGTH, MAX_STORY_REPLY_LENGTH, MAX_TASK_TITLE_LENGTH, MAX_WHISPER_LENGTH, NOTIFICATION_LEVELS, POLL_MAX_OPTIONS, POLL_MAX_OPTION_LENGTH, POLL_MAX_QUESTION_LENGTH, ROLES, WIDGET_KINDS, WIDGET_SURFACES, SPACE_TEMPLATES, TASK_PRIORITIES, TASK_RECURRENCES, TASK_STATUSES, TASK_TYPES, MILESTONE_STATUSES, CUSTOM_FIELD_TYPES, MAX_PROJECT_CUSTOM_FIELDS, PRESENCE_STATUSES, QUIET_HOURS_MODES, DND_INDEFINITE_MINUTES, WAITLIST_PLATFORMS, WORKSPACE_INDUSTRIES, ZONE_KINDS, } from './constants.js';
import { GAME_KINDS } from './games.js';
import { TRANSLATION_LANGUAGE_CODES } from './language.js';
import { COUNTRIES, isValidTimezone } from './time.js';
import { isValidE164 } from './phone.js';
import { isValidJoinCode, normalizeJoinCode } from './joinCode.js';
import { API_KEY_SCOPES, ASSISTANT_SUBJECTS, CHAT_CONTEXT_RANGES, CURRENCIES, EXPENSE_CATEGORIES, EXPENSE_STATUSES, FINANCE_READ_TARGETS, FINANCE_SCOPES, FILE_VISIBILITIES, GENERATED_FILE_BRIEF_MAX, GENERATED_FILE_LENGTHS, JOIN_POLICIES, LIBRARY_FILE_KINDS, LEAVE_KINDS, STATEMENT_KINDS, MEETING_CATEGORIES, POLICY_KINDS, REMINDER_CATEGORIES, SPACE_ACTIVITIES, SPACE_AGENT_ROLES, PROJECT_AGENT_MAX_FOLLOW_UPS, PROJECT_AGENT_MAX_SOURCES, PROJECT_AGENT_SOURCE_KINDS, WORKSPACE_FEATURES, } from './types.js';
export const objectId = z.string().regex(/^[a-f\d]{24}$/i, 'Invalid id');
/**
 * A language this deployment can translate to. The empty string is a value,
 * not a miss: it is how someone says "leave chat in whatever it was written
 * in", which is the default and the only way back out of a choice.
 */
export const translationLanguage = z
    .string()
    .refine((value) => value === '' || TRANSLATION_LANGUAGE_CODES.includes(value), 'Unsupported language');
export const hexColor = z.string().regex(/^#([\da-f]{3}|[\da-f]{6})$/i, 'Invalid color');
/**
 * A zone the runtime can actually format in, rather than any 64 characters.
 *
 * Checked against `Intl` instead of the country table so tzdata's backward
 * links ("US/Eastern") — which older clients and some phone locales still
 * report — are accepted rather than rejected as typos.
 */
export const timezone = z.string().trim().max(64).refine(isValidTimezone, 'Unknown timezone');
/** An empty string clears the setting; anything else must be a real zone. */
export const optionalTimezone = timezone.or(z.literal(''));
export const countryCode = z
    .string()
    .trim()
    .toUpperCase()
    .refine((code) => COUNTRIES.some((c) => c.code === code), 'Unknown country');
export const optionalCountryCode = countryCode.or(z.literal(''));
/**
 * A phone number already in E.164 — `+` then digits, no spaces or brackets.
 *
 * Clients assemble it from a country picker and a number box rather than
 * asking anyone to type the `+`, so this is the backstop that keeps a stored
 * number in one shape whatever the form did.
 */
export const phoneNumber = z
    .string()
    .trim()
    .refine(isValidE164, 'Enter a valid phone number, including the country code');
/** An empty string means "not given" and clears any number already stored. */
export const optionalPhoneNumber = phoneNumber.or(z.literal(''));
/**
 * A workspace join code, accepted in whatever shape it was pasted.
 *
 * The transform runs before the check so the server only ever sees the
 * canonical form: routes look codes up by exact match, and a lookup that had
 * to cope with hyphenation would be a lookup that could miss.
 */
export const joinCode = z
    .string()
    .trim()
    .transform(normalizeJoinCode)
    .refine(isValidJoinCode, 'That workspace code does not look right');
export const vec2Schema = z.object({ x: z.number(), y: z.number() });
export const rectSchema = z.object({
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
});
/* ------------------------------- auth ---------------------------------- */
export const registerSchema = z.object({
    name: z.string().trim().min(2).max(80),
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(8, 'Password must be at least 8 characters').max(128),
    /**
     * Optional, and stored rather than acted on: an owner reviewing a join
     * request often needs to reach the person before approving it. Older
     * clients that never send one keep working, which is why this is not
     * required even though both forms now ask for it.
     */
    phone: optionalPhoneNumber.optional(),
    orgName: z.string().trim().min(2).max(80).optional(),
    /**
     * What line of work the workspace being asked for is in. A hint rather than
     * a decision: the platform admin reviewing the request confirms or corrects
     * it on approval, and it is what decides which starting layouts the new
     * workspace is offered.
     */
    orgIndustry: z.enum(WORKSPACE_INDUSTRIES).optional(),
    /**
     * Which workspace to join, named by a code its owner handed out rather than
     * by an id taken from a public list. Registration deliberately has no way
     * to name a workspace it was not given the code for.
     */
    joinCode: joinCode.optional(),
    /**
     * Accepted only to be refused. Clients shipped before join codes named the
     * workspace by id, and zod strips keys it does not declare — so leaving this
     * out would turn "let me join this workspace" from an old build into a
     * request for a brand new one, silently and under the wrong name. Declaring
     * it lets the route tell those clients to update instead.
     *
     * @deprecated Removed with the public workspace list. Use `joinCode`.
     */
    orgId: objectId.optional(),
    inviteToken: z.string().optional(),
});
export const loginSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(1),
});
export const forgotPasswordSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
});
export const resetPasswordSchema = z.object({
    token: z.string().trim().min(16),
    password: z.string().min(8, 'Password must be at least 8 characters'),
});
export const refreshSchema = z.object({ refreshToken: z.string().min(10) });
/**
 * A calendar day without a year. Day 31 is allowed for any month and Feb 30
 * is not — the check is against the longest month a day number can have,
 * with Feb 29 permitted because it is somebody's birthday every four years.
 */
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
export const birthdaySchema = z
    .object({
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
})
    .refine((value) => value.day <= DAYS_IN_MONTH[value.month - 1], 'That day does not exist');
export const updateProfileSchema = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    title: z.string().trim().max(120).optional(),
    timezone: timezone.optional(),
    /**
     * Off means "I picked this zone myself, stop moving it". The client keeps
     * sending the detected zone while it is on, so travelling still updates it.
     */
    autoTimezone: z.boolean().optional(),
    country: optionalCountryCode.optional(),
    /** The language they read chat in; "" turns translation off for them. */
    language: translationLanguage.optional(),
    avatarUrl: z.string().url().optional().or(z.literal('')),
    avatarColor: hexColor.optional(),
    statusMessage: z.string().max(140).optional(),
    status: z.enum(PRESENCE_STATUSES).optional(),
    /** Month and day, no year; `null` forgets it. */
    birthday: birthdaySchema.nullable().optional(),
    /** Off opts out of birthday and anniversary announcements. */
    celebrations: z.boolean().optional(),
    /** Off takes you out of the daily coffee draw, and only out of that. */
    coffeePairing: z.boolean().optional(),
    /** Off hides your read cursor from others — and theirs from you. */
    readReceipts: z.boolean().optional(),
});
/** An explicit target language, or nothing for "the one I read chat in". */
export const translateMessageSchema = z.object({
    language: translationLanguage.optional(),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: z.string().min(8).max(128),
});
export const deleteAccountSchema = z.object({
    password: z.string().min(1),
});
/* ------------------------------- orgs ---------------------------------- */
export const createOrgSchema = z.object({
    name: z.string().trim().min(2).max(80),
});
/** The reviewer's chance to correct the industry the requester declared. */
export const approveWorkspaceRequestSchema = z.object({
    industry: z.enum(WORKSPACE_INDUSTRIES).optional(),
});
/** A platform admin moving an existing workspace to another industry. */
export const setWorkspaceIndustrySchema = z.object({
    industry: z.enum(WORKSPACE_INDUSTRIES),
});
/**
 * A platform admin creating a workspace outright, with no request behind it.
 *
 * The owner is named by address rather than by id because that is what an
 * administrator has to hand: an address already on the system is used as it
 * stands, and an unknown one is created here — a workspace with no owner is
 * a workspace nobody can administer, so it is never left without one.
 */
export const adminCreateWorkspaceSchema = z
    .object({
    name: z.string().trim().min(2).max(80),
    industry: z.enum(WORKSPACE_INDUSTRIES).default('general'),
    ownerEmail: z.string().trim().toLowerCase().email(),
    /** Both required together, and only when `ownerEmail` is a new account. */
    ownerName: z.string().trim().min(2).max(80).optional(),
    ownerPassword: z.string().min(8).max(128).optional(),
})
    .refine((body) => Boolean(body.ownerName) === Boolean(body.ownerPassword), {
    message: 'A new owner needs both a name and a password',
    path: ['ownerPassword'],
});
/**
 * Deleting a workspace erases everything inside it for everyone in it, so the
 * caller types the name back. A confirmation the client could fill in on the
 * caller's behalf would not be one.
 */
export const deleteWorkspaceSchema = z.object({
    confirm: z.string().trim().min(1),
});
export const rejectWorkspaceRequestSchema = z.object({
    /** Shown to the requester when they next try to sign in, so keep it usable. */
    reason: z.string().trim().max(400).optional(),
});
export const inviteMemberSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    // Ownership is transferred by an existing owner through membership
    // management, never granted by an invitation link or direct API request.
    role: z.enum(['admin', 'member', 'guest']).default('member'),
});
/** Creates a ready-to-use account directly from workspace administration. */
export const createMemberAccountSchema = z.object({
    name: z.string().trim().min(2).max(80),
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(8).max(128),
    title: z.string().trim().max(120).optional(),
    timezone: timezone.default('UTC'),
    country: optionalCountryCode.optional(),
    role: z.enum(['admin', 'member', 'guest']).default('member'),
});
export const updateMemberSchema = z.object({
    role: z.enum(ROLES),
});
export const updateMemberFeaturesSchema = z.object({
    features: z.array(z.enum(WORKSPACE_FEATURES)).max(WORKSPACE_FEATURES.length),
});
/**
 * Access changed for a selection of people in a single request.
 *
 * `mode` is what separates the two things administration actually does with
 * this screen. A preset is a replacement — "these are your products now" —
 * while granting one product to five people must leave whatever else they
 * hold alone. Doing both through `replace` is how "give them chat" used to
 * take away meetings, so add and remove are their own modes rather than
 * something the caller reconstructs by sending the whole set back.
 *
 * Defaults to `replace` so the presets that predate this keep their meaning.
 */
export const bulkMemberFeaturesSchema = z.object({
    userIds: z.array(objectId).min(1).max(200),
    features: z.array(z.enum(WORKSPACE_FEATURES)).max(WORKSPACE_FEATURES.length),
    mode: z.enum(['replace', 'add', 'remove']).default('replace'),
});
/**
 * A bare hostname — no scheme, no path, no `@`. Owners type "company.com",
 * and pasting an address instead is the mistake worth catching here.
 */
const emailDomain = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z\d]([a-z\d-]*[a-z\d])?(\.[a-z\d]([a-z\d-]*[a-z\d])?)+$/, 'Use a domain like company.com');
export const workspaceSettingsSchema = z.object({
    disabledFeatures: z
        .array(z.enum(WORKSPACE_FEATURES))
        .max(WORKSPACE_FEATURES.length)
        .optional(),
    joinPolicy: z.enum(JOIN_POLICIES).optional(),
    allowedEmailDomains: z.array(emailDomain).max(20).optional(),
    translation: z.object({ enabled: z.boolean(), automatic: z.boolean() }).partial().optional(),
    /**
     * `kudosEmoji` is a Unicode emoji or a `:name:` custom one — the same
     * string a reaction carries, since that is what it is compared against.
     * Null switches reaction Appys off.
     */
    engagement: z
        .object({ kudosEmoji: z.string().trim().min(1).max(32).nullable() })
        .partial()
        .optional(),
    /**
     * The booking horizon, in days. One is the smallest honest value — "today
     * only" — and a year is as far ahead as a floor plan means anything.
     */
    booking: z
        .object({ maxDaysAhead: z.number().int().min(1).max(365) })
        .partial()
        .optional(),
    /**
     * The daily coffee draw. `days` is a set of weekday numbers rather than a
     * cron string because the only schedule this feature has ever needed is
     * "these days, at this hour"; an empty list is a workspace that has switched
     * every day off, which is a slower way of saying `enabled: false` and is
     * accepted as such. Empty strings clear the floor and the room, putting the
     * draw back on whichever lounge the service can find.
     */
    coffee: z
        .object({
        enabled: z.boolean(),
        days: z.array(z.number().int().min(0).max(6)).max(7),
        time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm'),
        spaceId: objectId.or(z.literal('')),
        zoneId: z.string().trim().max(80),
    })
        .partial()
        .optional(),
    /** Empty string clears it, putting the workspace back to "no declared zone". */
    timezone: optionalTimezone.optional(),
    country: optionalCountryCode.optional(),
    currency: z.enum(CURRENCIES).optional(),
    /**
     * Accepted here so one schema describes the whole settings document, but the
     * orgs route rejects it from anyone but a platform admin — an owner who
     * could set their own industry could pick any starting layout in the
     * catalogue, which is the thing the industry exists to decide.
     */
    industry: z.enum(WORKSPACE_INDUSTRIES).optional(),
    /**
     * How many floors the workspace may have, zero for no ceiling. Accepted here
     * with the industry, and refused from anyone but a platform admin by the
     * same guard: an owner who could raise their own limit does not have one.
     */
    spaceLimit: z.number().int().min(0).max(500).optional(),
    /** Empty puts the workspace back under its industry's catalogue. */
    allowedTemplates: z.array(z.enum(SPACE_TEMPLATES)).max(SPACE_TEMPLATES.length).optional(),
});
export const updateOrgSchema = z
    .object({
    name: z.string().trim().min(2).max(80).optional(),
    // An empty string clears the logo; anything else must be a real URL.
    logoUrl: z.string().trim().url().max(500).optional().or(z.literal('')),
    settings: workspaceSettingsSchema.optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'At least one field is required');
export const ownerUpdateUserSchema = z
    .object({
    name: z.string().trim().min(2).max(80).optional(),
    email: z.string().trim().toLowerCase().email().optional(),
    title: z.string().trim().max(120).optional(),
    timezone: timezone.optional(),
    country: optionalCountryCode.optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'At least one field is required');
export const ownerResetPasswordSchema = z.object({
    newPassword: z.string().min(8, 'Password must be at least 8 characters').max(128),
});
/* -------------------------- team management ----------------------------- */
/** A calendar day with no clock or timezone attached. */
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-16');
const timeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a time like 09:00');
export const createAnnouncementSchema = z.object({
    title: z.string().trim().min(2).max(140),
    body: z.string().trim().max(4000).optional(),
    requireAck: z.boolean().default(true),
});
export const checkInConfigSchema = z.object({
    enabled: z.boolean(),
    question: z.string().trim().min(3).max(200),
    /** Empty string switches the reminder off. */
    remindAt: timeOfDay.or(z.literal('')).default(''),
    timezone: timezone.default('UTC'),
});
export const checkInSubmitSchema = z.object({
    body: z.string().trim().min(1).max(2000),
});
export const createLeaveRequestSchema = z
    .object({
    kind: z.enum(LEAVE_KINDS),
    startDate: dateOnly,
    endDate: dateOnly,
    note: z.string().trim().max(500).optional(),
})
    .refine((body) => body.endDate >= body.startDate, 'Leave cannot end before it starts');
export const reviewLeaveRequestSchema = z.object({
    status: z.enum(['approved', 'rejected']),
    note: z.string().trim().max(500).optional(),
});
export const workScheduleSchema = z.object({
    days: z.array(z.number().int().min(0).max(6)).min(1).max(7),
    startTime: timeOfDay,
    endTime: timeOfDay,
    timezone: timezone.default('UTC'),
    alertLateMinutes: z.number().int().min(0).max(480).default(0),
});
export const officeSiteSchema = z.object({
    name: z.string().trim().min(2).max(80),
    address: z.string().trim().max(200).optional(),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    radiusMeters: z.number().int().min(25).max(2000).default(150),
});
export const officeAssignmentSchema = z.object({
    /** An empty list clears the assignment — "any office". */
    siteIds: z.array(objectId).max(50).optional(),
    /**
     * The one-place shape this endpoint used to take. Kept so a browser tab
     * left open across the deploy keeps working; `siteIds` wins when both are
     * sent, and null still means "any office".
     */
    siteId: objectId.nullable().optional(),
});
export const officeCheckInSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    /** GPS accuracy in meters, granted as slack on top of the site radius. */
    accuracy: z.number().min(0).max(10_000).optional(),
    method: z.enum(['manual', 'geofence']).default('manual'),
});
/**
 * Leaving is reported after the fact: the phone asks the member to confirm
 * before it checks them out, and if the phone was asleep the confirmation can
 * only run when it next wakes. `at` is when they actually crossed the fence.
 */
export const officeCheckOutSchema = z.object({
    at: z.string().datetime().optional(),
    /** Whether the member tapped check out or their phone's geofence decided. */
    method: z.enum(['manual', 'geofence']).default('manual'),
});
export const onboardingStepInputSchema = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1000).optional(),
    link: z.string().trim().max(500).optional(),
});
export const onboardingTemplateSchema = z.object({
    name: z.string().trim().min(2).max(80),
    isDefault: z.boolean().default(false),
    steps: z.array(onboardingStepInputSchema).min(1).max(50),
});
export const onboardingToggleStepSchema = z.object({
    done: z.boolean(),
});
export const createSurveySchema = z.object({
    question: z.string().trim().min(5).max(300),
    anonymous: z.boolean().default(true),
    /** How long the survey stays open; unset means until closed by hand. */
    closesInDays: z.number().int().min(1).max(30).optional(),
});
export const surveyRespondSchema = z.object({
    score: z.number().int().min(1).max(5),
    comment: z.string().trim().max(500).optional(),
});
/** Null clears the expiry — the membership becomes permanent again. */
export const memberExpirySchema = z.object({
    expiresAt: z.string().datetime().nullable(),
});
/**
 * The day someone started with the company, as a plain YYYY-MM-DD: a start
 * date is a calendar fact, not an instant, and `null` goes back to counting
 * from the day they joined the workspace.
 */
export const memberStartDateSchema = z.object({
    startedAt: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
        .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'Not a real date')
        .nullable(),
});
/* ---------------------------- hierarchy -------------------------------- */
export const departmentSchema = z.object({
    name: z.string().trim().min(2).max(80),
    description: z.string().trim().max(500).optional(),
    /** Null keeps the department at the top level. */
    parentId: objectId.nullable().optional(),
    /** The user id of the department head; null leaves it vacant. */
    leadId: objectId.nullable().optional(),
});
export const updateDepartmentSchema = departmentSchema.partial();
/**
 * Where one person sits. Both fields are tri-state on purpose: absent leaves
 * the value alone, so the department picker and the manager picker can be
 * saved independently, and null clears it.
 */
export const memberPlacementSchema = z
    .object({
    departmentId: objectId.nullable().optional(),
    managerId: objectId.nullable().optional(),
})
    .refine((body) => body.departmentId !== undefined || body.managerId !== undefined, 'Nothing to change');
/* ----------------------------- policies -------------------------------- */
export const createPolicySchema = z.object({
    kind: z.enum(POLICY_KINDS).default('policy'),
    title: z.string().trim().min(2).max(140),
    summary: z.string().trim().max(300).optional(),
    body: z.string().trim().min(1).max(60_000),
    requireAck: z.boolean().default(true),
    order: z.number().int().min(0).max(999).default(0),
});
/**
 * Every field optional: the editor saves a body without restating the kind,
 * and the list reorders without sending the text of the document back.
 */
export const updatePolicySchema = createPolicySchema.partial();
export const meetingNoteSchema = z.object({
    body: z.string().max(10000),
});
/**
 * Closing a recording. The duration comes from the recorder's own clock
 * rather than being inferred from the bytes — a webm written slice by slice
 * has no reliable length header for the server to read.
 */
export const finishMeetingRecordingSchema = z.object({
    durationSec: z.number().min(0).max(MEETING_RECORDING_MAX_SECONDS).optional(),
});
/* ------------------------------ finance -------------------------------- */
/**
 * An amount in minor units. Integer because that is how it is stored, and
 * capped at a trillion minor units — ten billion in the major unit — so a
 * fat-fingered figure is rejected at the door rather than swamping every
 * chart it lands in.
 */
const moneyAmount = z.number().int().nonnegative().max(1_000_000_000_000);
/**
 * A scoped record points at exactly the id its scope names: a department
 * budget without a department is unattributable, and a workspace budget
 * carrying one would count twice — once against the company and once against
 * the department.
 */
const scopedToItsId = (body) => {
    if (body.scope === 'department')
        return Boolean(body.departmentId) && !body.projectId;
    if (body.scope === 'project')
        return Boolean(body.projectId) && !body.departmentId;
    if (body.scope === 'workspace')
        return !body.departmentId && !body.projectId;
    return true;
};
const SCOPE_MESSAGE = 'Choose the department or project this is charged to';
export const createBudgetSchema = z
    .object({
    name: z.string().trim().min(2).max(80),
    scope: z.enum(FINANCE_SCOPES).default('workspace'),
    departmentId: objectId.optional(),
    projectId: objectId.optional(),
    amount: moneyAmount,
    startDate: dateOnly,
    endDate: dateOnly,
    note: z.string().trim().max(500).optional(),
})
    .refine((body) => body.endDate >= body.startDate, 'A budget cannot end before it starts')
    .refine(scopedToItsId, SCOPE_MESSAGE);
/**
 * Partial so the amount can be revised without restating the window, but the
 * two cross-field rules are re-checked: a patch that moves only `endDate` can
 * still invert the range, and one that moves only `scope` can still orphan the
 * id it used to point at.
 */
export const updateBudgetSchema = z
    .object({
    name: z.string().trim().min(2).max(80).optional(),
    scope: z.enum(FINANCE_SCOPES).optional(),
    departmentId: objectId.nullable().optional(),
    projectId: objectId.nullable().optional(),
    amount: moneyAmount.optional(),
    startDate: dateOnly.optional(),
    endDate: dateOnly.optional(),
    note: z.string().trim().max(500).optional(),
})
    .refine((body) => body.startDate === undefined ||
    body.endDate === undefined ||
    body.endDate >= body.startDate, 'A budget cannot end before it starts')
    .refine((body) => body.scope === undefined || scopedToItsId(body), SCOPE_MESSAGE);
/**
 * Both halves of a foreign amount travel together or not at all: an original
 * figure with no currency cannot be read, and a currency with no figure says
 * nothing. The converted `amount` is always required regardless — it is what
 * the books add up.
 */
const foreignPair = (body) => (body.originalAmount === undefined || body.originalAmount === null) ===
    (body.originalCurrency === undefined || body.originalCurrency === null);
const FOREIGN_MESSAGE = 'Give both the original amount and its currency, or neither';
export const createExpenseSchema = z
    .object({
    description: z.string().trim().min(2).max(200),
    vendor: z.string().trim().max(120).optional(),
    amount: moneyAmount,
    /** Nullable as well as optional so one client payload shape serves both
        creating and clearing, rather than two that differ only in absence. */
    originalAmount: moneyAmount.nullable().optional(),
    originalCurrency: z.enum(CURRENCIES).nullable().optional(),
    category: z.enum(EXPENSE_CATEGORIES).default('other'),
    date: dateOnly,
    scope: z.enum(FINANCE_SCOPES).default('workspace'),
    departmentId: objectId.optional(),
    projectId: objectId.optional(),
    /** A document already uploaded through `POST /finance/files`. */
    receiptId: objectId.optional(),
})
    .refine(scopedToItsId, SCOPE_MESSAGE)
    .refine(foreignPair, FOREIGN_MESSAGE);
export const updateExpenseSchema = z
    .object({
    description: z.string().trim().min(2).max(200).optional(),
    vendor: z.string().trim().max(120).optional(),
    amount: moneyAmount.optional(),
    /** Null on either clears the pair — the row becomes a plain one. */
    originalAmount: moneyAmount.nullable().optional(),
    originalCurrency: z.enum(CURRENCIES).nullable().optional(),
    category: z.enum(EXPENSE_CATEGORIES).optional(),
    date: dateOnly.optional(),
    scope: z.enum(FINANCE_SCOPES).optional(),
    departmentId: objectId.nullable().optional(),
    projectId: objectId.nullable().optional(),
    /** Null detaches the receipt; absent leaves whatever is on file. */
    receiptId: objectId.nullable().optional(),
})
    .refine((body) => body.scope === undefined || scopedToItsId(body), SCOPE_MESSAGE)
    .refine(foreignPair, FOREIGN_MESSAGE);
export const reviewExpenseSchema = z.object({
    status: z.enum(['approved', 'rejected']),
    note: z.string().trim().max(500).optional(),
});
export const expenseQuerySchema = z.object({
    status: z.enum(EXPENSE_STATUSES).optional(),
    category: z.enum(EXPENSE_CATEGORIES).optional(),
    departmentId: objectId.optional(),
    projectId: objectId.optional(),
    from: dateOnly.optional(),
    to: dateOnly.optional(),
});
export const createStatementSchema = z
    .object({
    title: z.string().trim().min(2).max(140),
    kind: z.enum(STATEMENT_KINDS).default('other'),
    periodStart: dateOnly,
    periodEnd: dateOnly,
    scope: z.enum(FINANCE_SCOPES).default('workspace'),
    departmentId: objectId.optional(),
    projectId: objectId.optional(),
    note: z.string().trim().max(500).optional(),
    fileId: objectId,
})
    .refine((body) => body.periodEnd >= body.periodStart, 'A period cannot end before it starts')
    .refine(scopedToItsId, SCOPE_MESSAGE);
export const statementQuerySchema = z.object({
    kind: z.enum(STATEMENT_KINDS).optional(),
    departmentId: objectId.optional(),
    projectId: objectId.optional(),
});
/** Which form the uploaded document is being read for. */
export const financeReadSchema = z.object({ target: z.enum(FINANCE_READ_TARGETS) });
export const financeWindowSchema = z
    .object({ from: dateOnly, to: dateOnly })
    .refine((body) => body.to >= body.from, 'The window cannot end before it starts');
/* ------------------------------ spaces --------------------------------- */
export const zoneSchema = z.object({
    id: z.string().optional(),
    name: z.string().trim().min(1).max(60),
    kind: z.enum(ZONE_KINDS),
    rect: rectSchema,
    color: hexColor,
    private: z.boolean().default(false),
    capacity: z.number().int().positive().max(500).optional(),
    targetZoneId: z.string().optional(),
    targetSpaceId: objectId.optional(),
    embedUrl: z.string().url().optional().or(z.literal('')),
    whiteboardId: objectId.optional(),
});
export const furnitureSchema = z.object({
    id: z.string().optional(),
    kind: z.string().min(1).max(40),
    pos: vec2Schema,
    w: z.number().positive(),
    h: z.number().positive(),
    rotation: z.number().default(0),
    solid: z.boolean().default(false),
    label: z.string().max(60).optional(),
    color: hexColor.optional(),
});
export const createSpaceSchema = z
    .object({
    name: z.string().trim().min(2).max(80),
    description: z.string().max(500).optional(),
    cols: z.number().int().min(20).max(200).optional(),
    rows: z.number().int().min(20).max(200).optional(),
    template: z.enum(SPACE_TEMPLATES).optional(),
    /** A floor plan someone in this org saved. Wins over `template`. */
    templateId: objectId.optional(),
})
    .refine((body) => !(body.template && body.templateId), {
    message: 'Choose either a built-in layout or a saved one, not both',
    path: ['templateId'],
});
export const saveSpaceTemplateSchema = z.object({
    name: z.string().trim().min(2).max(80),
    description: z.string().max(500).optional(),
});
export const updateSpaceSchema = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    description: z.string().max(500).optional(),
    cols: z.number().int().min(20).max(200).optional(),
    rows: z.number().int().min(20).max(200).optional(),
    backgroundColor: hexColor.optional(),
    backgroundImageUrl: z.string().url().optional().or(z.literal('')),
    musicUrl: z.string().url().optional().or(z.literal('')),
    spawn: vec2Schema.optional(),
    zones: z.array(zoneSchema).max(200).optional(),
    furniture: z.array(furnitureSchema).max(1000).optional(),
    activities: z.array(z.enum(SPACE_ACTIVITIES)).max(SPACE_ACTIVITIES.length).optional(),
});
/**
 * Who a layout is offered to. Empty lists are the default — a restriction
 * nobody has written yet, rather than a layout offered to nobody.
 */
export const templateAudienceSchema = z.object({
    orgIds: z.array(objectId).max(500).optional(),
    userIds: z.array(objectId).max(500).optional(),
});
/** Opening or closing one floor, from platform administration. */
export const setSpaceActiveSchema = z.object({
    active: z.boolean(),
});
/**
 * Floors a platform admin is adding to a workspace, each with the layout it is
 * built from.
 *
 * A list rather than a count and one layout, because the reason to add three
 * floors at once is usually that they are different: a reception, a ward and a
 * staff room, not three identical offices. Names are still checked one by one
 * against the workspace, so a half-valid batch fails before anything is built.
 */
export const adminCreateSpacesSchema = z.object({
    spaces: z
        .array(z.object({
        name: z.string().trim().min(2).max(80),
        description: z.string().max(500).optional(),
        template: z.enum(SPACE_TEMPLATES).default('startup_office'),
    }))
        .min(1)
        .max(20),
});
/**
 * Editing a saved layout as a platform administrator: what it is called, who
 * it is offered to, and whether it belongs to one workspace or the install.
 */
export const adminUpdateSpaceTemplateSchema = z
    .object({
    name: z.string().trim().min(2).max(80).optional(),
    description: z.string().max(500).optional(),
    active: z.boolean().optional(),
    /** True lifts the layout out of its workspace and offers it to everyone. */
    global: z.boolean().optional(),
    audience: templateAudienceSchema.optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'At least one field is required');
/** The rule written over a built-in layout, which replaces its industry match. */
export const builtinTemplateRuleSchema = z
    .object({
    active: z.boolean().optional(),
    audience: templateAudienceSchema.optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'At least one field is required');
/**
 * The order one person keeps their spaces in.
 *
 * Personal, not organisational: putting the room you live in first should not
 * rearrange the floor for everyone else. The list may be partial — a client
 * only ever sends the spaces it can see — so anything unlisted keeps its
 * relative place after the rest.
 */
export const reorderSpacesSchema = z.object({
    ids: z.array(objectId).max(200),
});
export const addSpaceMemberSchema = z.object({
    userId: objectId,
    zoneId: z.string().min(1).max(64).optional(),
    furnitureId: z.string().min(1).max(64).optional(),
});
export const updateSpaceMemberSchema = z
    .object({
    // The member's assigned office/desk zone; null clears the assignment.
    zoneId: z.string().min(1).max(64).nullable().optional(),
    furnitureId: z.string().min(1).max(64).nullable().optional(),
})
    .refine((body) => body.zoneId !== undefined || body.furnitureId !== undefined, {
    message: 'Provide an office or desk assignment',
});
/* ----------------------------- projects -------------------------------- */
export const createProjectSchema = z.object({
    name: z.string().trim().min(2).max(80),
    key: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z][A-Z0-9]{1,7}$/, 'Key must be 2-8 uppercase letters/digits')
        .optional(),
    description: z.string().max(1000).optional(),
    color: hexColor.optional(),
    memberIds: z.array(objectId).optional(),
    /**
     * Makes the board the author's alone. `memberIds` is ignored when it is
     * set — a private board with a second member on it is not private, and
     * silently dropping the extras is kinder than a validation error for a
     * combination no UI offers.
     */
    personal: z.boolean().optional(),
});
export const wipLimitsSchema = z.record(z.enum(TASK_STATUSES), z.number().int().min(0).max(99));
export const projectCustomFieldSchema = z
    .object({
    /** Omitted on a brand-new field; the server mints one. */
    id: z.string().max(64).optional(),
    name: z.string().trim().min(1).max(60),
    type: z.enum(CUSTOM_FIELD_TYPES),
    options: z.array(z.string().trim().min(1).max(60)).max(30).optional(),
})
    .refine((field) => field.type !== 'select' || (field.options?.length ?? 0) > 0, {
    message: 'A select field needs at least one option',
});
/**
 * `personal` is deliberately absent: privacy is decided once, when the board
 * is created. Flipping it later would either expose tasks written in
 * confidence, or hide a shared board from the team still working on it.
 */
export const updateProjectSchema = createProjectSchema
    .partial()
    .omit({ personal: true })
    .extend({
    archived: z.boolean().optional(),
    wipLimits: wipLimitsSchema.optional(),
    customFields: z.array(projectCustomFieldSchema).max(MAX_PROJECT_CUSTOM_FIELDS).optional(),
});
/* ----------------------------- milestones ------------------------------- */
export const createMilestoneSchema = z.object({
    title: z.string().trim().min(1).max(120),
    description: z.string().max(2000).optional(),
    dueDate: z.string().datetime().optional().or(z.literal('')),
});
export const updateMilestoneSchema = createMilestoneSchema.partial().extend({
    status: z.enum(MILESTONE_STATUSES).optional(),
});
/* ------------------------------- tasks --------------------------------- */
export const checklistItemSchema = z.object({
    id: z.string().optional(),
    text: z.string().trim().min(1).max(300),
    done: z.boolean().default(false),
});
export const createTaskSchema = z.object({
    projectId: objectId,
    title: z.string().trim().min(1).max(MAX_TASK_TITLE_LENGTH),
    description: z.string().max(20000).optional(),
    status: z.enum(TASK_STATUSES).default('todo'),
    priority: z.enum(TASK_PRIORITIES).default('none'),
    type: z.enum(TASK_TYPES).default('task'),
    assigneeIds: z.array(objectId).max(20).optional(),
    labels: z.array(z.string().max(40)).max(20).optional(),
    /** Empty string detaches the task, mirroring how dates are cleared. */
    milestoneId: objectId.optional().or(z.literal('')),
    /** Values keyed by the project field's id; null clears a value. */
    customFields: z
        .record(z.string().max(64), z.union([z.string().max(500), z.number(), z.null()]))
        .optional(),
    dueDate: z.string().datetime().optional().or(z.literal('')),
    startDate: z.string().datetime().optional().or(z.literal('')),
    estimate: z.number().min(0).max(1000).optional(),
    checklist: z.array(checklistItemSchema).max(100).optional(),
    parentTaskId: objectId.optional(),
    recurrence: z.enum(TASK_RECURRENCES).default('none'),
    /** Each assignee finishes their own copy; the task closes on the last one. */
    eachAssigneeCompletes: z.boolean().default(false),
});
export const updateTaskSchema = createTaskSchema
    .partial()
    .omit({ projectId: true })
    .extend({
    order: z.number().optional(),
    attachmentIds: z.array(objectId).optional(),
    reporterId: objectId.optional(),
});
export const moveTaskSchema = z.object({
    status: z.enum(TASK_STATUSES),
    /** Index within the destination column, 0-based. */
    index: z.number().int().min(0),
    /** Set to bypass a soft WIP-limit warning after the user confirms. */
    force: z.boolean().optional(),
});
/* --------------------------- workflow extras --------------------------- */
export const dependencySchema = z.object({
    /** The task that must finish first. */
    blockerId: objectId,
});
export const logTimeSchema = z.object({
    minutes: z
        .number()
        .int()
        .min(1)
        .max(24 * 60),
    note: z.string().max(300).optional(),
    spentOn: z.string().datetime().optional(),
});
export const automationConditionSchema = z.object({
    field: z.enum(AUTOMATION_CONDITION_FIELDS),
    operator: z.enum(AUTOMATION_OPERATORS),
    value: z.string().max(200).optional(),
});
export const automationActionSchema = z.object({
    kind: z.enum(AUTOMATION_ACTIONS),
    value: z.string().max(500).optional(),
    values: z.array(z.string().max(200)).max(20).optional(),
    message: z.string().max(500).optional(),
});
const automationShape = z.object({
    name: z.string().trim().min(2).max(80),
    description: z.string().max(300).optional(),
    scope: z.enum(AUTOMATION_SCOPES).default('project'),
    projectId: objectId.optional(),
    spaceId: objectId.optional(),
    trigger: z.enum(AUTOMATION_TRIGGERS),
    conditions: z.array(automationConditionSchema).max(10).default([]),
    actions: z.array(automationActionSchema).min(1).max(10),
    enabled: z.boolean().default(true),
});
/**
 * Rejects a rule whose vocabulary is mixed — a zone trigger asked to assign a
 * task, or a task trigger asked to post about a room. The check is shared with
 * the server so a PATCH is judged the same way, against the scope the stored
 * rule already has.
 */
function refineAutomation(value, ctx) {
    // On a partial payload the scope may be absent; the trigger then names it,
    // since no trigger belongs to both.
    const scope = value.scope ?? (value.trigger ? automationScopeOfTrigger(value.trigger) : undefined);
    if (!scope)
        return;
    const error = automationCompatibilityError({
        scope,
        trigger: value.trigger,
        actions: value.actions,
    });
    if (error)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: error, path: ['actions'] });
}
export const createAutomationSchema = automationShape.superRefine(refineAutomation);
export const updateAutomationSchema = automationShape.partial().superRefine(refineAutomation);
export const taskQuerySchema = z.object({
    projectId: objectId.optional(),
    status: z.enum(TASK_STATUSES).optional(),
    assigneeId: objectId.optional(),
    priority: z.enum(TASK_PRIORITIES).optional(),
    type: z.enum(TASK_TYPES).optional(),
    milestoneId: objectId.optional(),
    label: z.string().optional(),
    q: z.string().max(200).optional(),
    dueBefore: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    cursor: z.string().optional(),
});
/* ------------------- attachments (chat and task comments) ---------------- */
export const messageAttachmentSchema = z.object({
    /* Not an objectId: an uploaded file's id is one, but a GIF carries the
       catalogue's id and a sticker carries its pack-local uuid. */
    id: z.string().min(1).max(64),
    name: z.string(),
    url: z.string(),
    mimeType: z.string(),
    size: z.number(),
    kind: z.enum(['file', 'voice', 'gif', 'sticker']).optional(),
    /* Pictures only, and advisory: they shape the bubble before the bytes
       arrive, so a wrong value costs a reflow rather than anything worse. */
    width: z.number().int().positive().max(100_000).optional(),
    height: z.number().int().positive().max(100_000).optional(),
    /* A 4x3-component hash is 28 characters; the cap leaves room without
       letting a client park arbitrary text in the message document. */
    blurhash: z.string().max(128).optional(),
    durationMs: z
        .number()
        .int()
        .min(0)
        .max(60 * 60 * 1000)
        .optional(),
    /* Capped so a pathological client can't push a megabyte of peaks into a
       message document; the recorder sends about sixty. */
    waveform: z.array(z.number().min(0).max(1)).max(256).optional(),
    transcript: z.string().max(MAX_MESSAGE_LENGTH).optional(),
    transcriptStatus: z.enum(['pending', 'ready', 'failed', 'unavailable']).optional(),
    transcriptSource: z.enum(['browser', 'server']).optional(),
});
/**
 * A task comment. The body may be empty when files came with it: dropping a
 * screenshot into the box and pressing send is a complete thought.
 */
export const commentSchema = z
    .object({
    body: z.string().trim().max(MAX_MESSAGE_LENGTH).default(''),
    attachments: z.array(messageAttachmentSchema).max(10).default([]),
})
    .refine((comment) => comment.body.length > 0 || comment.attachments.length > 0, {
    message: 'Write something or attach a file',
    path: ['body'],
});
/* ------------------------------ channels ------------------------------- */
export const createChannelSchema = z.object({
    name: z.string().trim().min(1).max(60),
    topic: z.string().max(250).optional(),
    isPrivate: z.boolean().default(false),
    memberIds: z.array(objectId).optional(),
});
export const createDmSchema = z.object({
    userIds: z.array(objectId).min(1).max(20),
    name: z.string().trim().min(1).max(60).optional(),
});
export const inviteChannelMembersSchema = z.object({
    userIds: z.array(objectId).min(1).max(100),
});
export const pollInputSchema = z.object({
    question: z.string().trim().min(1).max(POLL_MAX_QUESTION_LENGTH),
    options: z
        .array(z.string().trim().min(1).max(POLL_MAX_OPTION_LENGTH))
        .min(2)
        .max(POLL_MAX_OPTIONS),
    multiple: z.boolean().default(false),
    anonymous: z.boolean().default(false),
    closesAt: z.string().datetime().optional(),
});
export const sendMessageSchema = z.object({
    body: z.string().max(MAX_MESSAGE_LENGTH),
    parentId: objectId.optional(),
    attachments: z.array(messageAttachmentSchema).max(10).optional(),
    poll: pollInputSchema.optional(),
    /** The message being quoted; the server flattens it into an excerpt. */
    quotedMessageId: objectId.optional(),
    /**
     * Send later. The route diverts the whole message into a scheduled record
     * rather than posting it, so nothing appears in the channel until then.
     */
    sendAt: z.string().datetime().optional(),
});
export const scheduleMessageSchema = z.object({
    sendAt: z.string().datetime(),
});
export const editMessageSchema = z.object({
    body: z.string().trim().min(1).max(MAX_MESSAGE_LENGTH),
});
export const reactionSchema = z.object({
    /* Unicode emoji, or `:name:` naming one of the org's custom emoji. */
    emoji: z
        .string()
        .min(1)
        .max(MAX_EMOJI_NAME_LENGTH + 2),
});
/**
 * A reaction on a story. Sending the emoji you already left removes it, so one
 * endpoint covers reacting, changing your mind and taking it back.
 */
export const storyReactionSchema = z.object({
    emoji: z
        .string()
        .min(1)
        .max(MAX_EMOJI_NAME_LENGTH + 2),
});
/** A written reply to a story, delivered as a DM to its author. */
export const storyReplySchema = z.object({
    body: z.string().trim().min(1).max(MAX_STORY_REPLY_LENGTH),
});
export const pollVoteSchema = z.object({
    /* Empty clears your vote; more than one is refused unless the poll is
       multiple-choice, which the route checks against the stored poll. */
    optionIds: z.array(z.string().min(1).max(40)).max(POLL_MAX_OPTIONS),
});
/* ---------------------- saving, reminders, forwarding -------------------- */
export const saveMessageSchema = z.object({
    note: z.string().trim().max(500).optional(),
});
export const remindMeSchema = z.object({
    remindAt: z.string().datetime(),
});
/* A reminder someone writes for themselves. `category` defaults rather than
   being required so a client that only offers one kind still posts. */
export const createReminderSchema = z.object({
    title: z.string().trim().min(1).max(MAX_REMINDER_TITLE_LENGTH),
    note: z.string().trim().max(MAX_REMINDER_NOTE_LENGTH).optional(),
    category: z.enum(REMINDER_CATEGORIES).default('personal'),
    remindAt: z.string().datetime(),
});
/**
 * Editing one. `done` is the tick-box rather than a `doneAt` timestamp the
 * client would have to invent, and clearing it re-arms the reminder.
 */
export const updateReminderSchema = z
    .object({
    title: z.string().trim().min(1).max(MAX_REMINDER_TITLE_LENGTH).optional(),
    note: z.string().trim().max(MAX_REMINDER_NOTE_LENGTH).nullable().optional(),
    category: z.enum(REMINDER_CATEGORIES).optional(),
    remindAt: z.string().datetime().optional(),
    done: z.boolean().optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'Nothing to update');
/**
 * The list filter. Done reminders are left out unless asked for, and the flag
 * stays a string because it arrives in a query string — the validator hands
 * the route back exactly what it parsed, so a coerced boolean would have to be
 * a transform, and a transform makes input and output shapes disagree.
 */
export const reminderListSchema = z.object({
    category: z.enum(REMINDER_CATEGORIES).optional(),
    includeDone: z.enum(['true', 'false']).optional(),
});
export const forwardMessageSchema = z.object({
    channelIds: z.array(objectId).min(1).max(10),
    note: z.string().trim().max(MAX_MESSAGE_LENGTH).optional(),
});
export const messageToTaskSchema = z.object({
    projectId: objectId,
    title: z.string().trim().min(1).max(MAX_TASK_TITLE_LENGTH).optional(),
    assigneeIds: z.array(objectId).max(10).optional(),
});
/* ---------------------- threads and notification prefs ------------------- */
export const channelPrefsSchema = z.object({
    level: z.enum(NOTIFICATION_LEVELS).optional(),
    muted: z.boolean().optional(),
    pinned: z.boolean().optional(),
});
/** Reorders the pinned conversations, most important first. */
export const pinnedChannelOrderSchema = z.object({
    channelIds: z.array(objectId).max(50),
});
export const notificationSettingsSchema = z.object({
    keywords: z.array(z.string().trim().toLowerCase().min(2).max(40)).max(30).optional(),
    mutedUserIds: z.array(objectId).max(200).optional(),
    quietHours: z
        .object({
        /* 24-hour local clock times; `22:00`–`07:00` wraps midnight. */
        start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        timezone: timezone.optional(),
    })
        .nullable()
        .optional(),
    quietHoursMode: z.enum(QUIET_HOURS_MODES).optional(),
    /* `null` ends a pause; a past `until` is accepted and simply means "not paused". */
    dnd: z
        .object({ until: z.string().datetime({ offset: true }) })
        .nullable()
        .optional(),
});
/**
 * The short way to pause: a length rather than an end time, so a phone with a
 * skewed clock still gets the hour it asked for. `null` unpauses.
 */
export const dndSchema = z.object({
    minutes: z.number().int().min(1).max(DND_INDEFINITE_MINUTES).nullable(),
});
/* ---------------------------- search and links --------------------------- */
/**
 * Search accepts operators inline in `q` (`from:@id`, `in:#channel`,
 * `has:link`, `before:2026-01-01`). They are parsed server-side so every
 * client gets the same grammar instead of each building its own.
 */
export const messageSearchSchema = z.object({
    q: z.string().trim().max(300).default(''),
    channelId: objectId.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
    before: objectId.optional(),
});
/**
 * "Ask the channel": a question answered out of one conversation's history.
 *
 * A floor of three characters because anything shorter has no keywords to
 * retrieve on, and the answer would be drawn from recency alone.
 */
export const channelAskSchema = z.object({
    question: z.string().trim().min(3).max(500),
});
export const unfurlSchema = z.object({
    url: z.string().url().max(2048),
});
/** A workspace path (`/tasks/:id`), never a full URL — origins differ per deploy. */
export const internalUnfurlSchema = z.object({
    path: z.string().trim().min(2).max(1024).startsWith('/'),
});
export const channelBookmarksSchema = z.object({
    bookmarks: z
        .array(z.object({
        id: z.string().max(40).optional(),
        label: z.string().trim().min(1).max(60),
        url: z.string().url().max(2048),
        emoji: z.string().max(8).optional(),
    }))
        .max(20),
});
/* --------------------- custom emoji, stickers, GIFs ---------------------- */
export const emojiNameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(MAX_EMOJI_NAME_LENGTH)
    .regex(/^[a-z0-9_+-]+$/, 'Use letters, numbers, dashes and underscores');
export const createCustomEmojiSchema = z.object({
    name: emojiNameSchema,
    pack: z.string().trim().max(40).optional(),
});
export const createEmojiAliasSchema = z.object({
    name: emojiNameSchema,
    aliasFor: objectId,
});
export const createStickerPackSchema = z.object({
    name: z.string().trim().min(1).max(60),
});
export const gifSearchSchema = z.object({
    q: z.string().trim().max(120).default(''),
    limit: z.coerce.number().int().min(1).max(50).default(24),
    offset: z.coerce.number().int().min(0).max(500).default(0),
});
/* ------------------------------ transcripts ------------------------------ */
/**
 * A transcript the browser produced while recording. Accepted from the client
 * because the audio never leaves the machine that way — the server-side STT
 * path is a separate endpoint that reads the stored file.
 */
export const attachTranscriptSchema = z.object({
    attachmentId: objectId,
    transcript: z.string().trim().max(MAX_MESSAGE_LENGTH),
});
/* -------------------------------- whisper -------------------------------- */
export const whisperSchema = z.object({
    spaceId: objectId,
    targetUserId: objectId,
    body: z.string().trim().min(1).max(MAX_WHISPER_LENGTH),
});
/* -------------------------------- widgets -------------------------------- */
export const createWidgetSchema = z.object({
    kind: z.enum(WIDGET_KINDS),
    surface: z.enum(WIDGET_SURFACES),
    /* Personal dashboards scope to the caller, so the route fills this in. */
    scopeId: objectId.optional(),
    zoneId: z.string().max(60).optional(),
    title: z.string().trim().max(60).optional(),
    span: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
    rows: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
    config: z.record(z.unknown()).optional(),
});
export const updateWidgetSchema = z.object({
    title: z.string().trim().max(60).nullable().optional(),
    span: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
    rows: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
    order: z.number().optional(),
    config: z.record(z.unknown()).optional(),
});
export const reorderWidgetsSchema = z.object({
    ids: z.array(objectId).max(64),
});
/**
 * Hanging a widget on a screen prop. The kind is checked against the curated
 * wall list in the route rather than here, so the refusal can name the kind.
 */
export const bindScreenWidgetSchema = z.object({
    spaceId: objectId,
    /** A furniture id from the floor plan — a `uid()`, not an ObjectId. */
    furnitureId: z.string().trim().min(1).max(60),
    kind: z.enum(WIDGET_KINDS),
    title: z.string().trim().max(60).optional(),
    config: z.record(z.unknown()).optional(),
});
export const updateScreenWidgetSchema = z.object({
    title: z.string().trim().max(60).nullable().optional(),
    config: z.record(z.unknown()).optional(),
});
/**
 * Putting home back to a known state. `classic` restores the layout everyone
 * had before home became something you arrange; `empty` clears it and leaves
 * the picker. Either way the sections that were there are replaced rather
 * than added to, so the result is what the name says.
 */
export const applyHomeTemplateSchema = z.object({
    template: z.enum(['classic', 'empty']),
});
/* ---------------------------- whiteboards ------------------------------ */
export const whiteboardShapeSchema = z.object({
    id: z.string(),
    kind: z.enum(['path', 'rect', 'ellipse', 'arrow', 'text', 'note', 'image']),
    points: z.array(z.number()).max(20000).optional(),
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
    stroke: z.string().max(24),
    fill: z.string().max(24).optional(),
    strokeWidth: z.number().min(0).max(80),
    text: z.string().max(2000).optional(),
    fontSize: z.number().min(6).max(160).optional(),
    /** Images live in file storage — only http(s) asset URLs, never inline data. */
    src: z.string().url().max(2048).optional(),
    createdBy: z.string(),
    updatedAt: z.number(),
});
/**
 * A board hangs off a space, off a meeting, or off nothing. Accepting both
 * ids would leave the scope undecidable, so the contract rejects that here
 * rather than letting the server pick a winner.
 */
export const createWhiteboardSchema = z
    .object({
    name: z.string().trim().min(1).max(80),
    spaceId: objectId.optional(),
    meetingId: objectId.optional(),
})
    .refine((body) => !(body.spaceId && body.meetingId), {
    message: 'A board belongs to a space or a meeting, not both',
    path: ['meetingId'],
});
export const updateWhiteboardSchema = z.object({
    name: z.string().trim().min(1).max(80),
});
/** List filters. `mine` narrows to boards the caller opened. */
export const whiteboardQuerySchema = z.object({
    scope: z.enum(['free', 'space', 'meeting']).optional(),
    spaceId: objectId.optional(),
    meetingId: objectId.optional(),
    mine: z
        .union([z.literal('1'), z.literal('true'), z.literal('0'), z.literal('false')])
        .optional(),
});
/* ---------------------------- spreadsheets ----------------------------- */
/**
 * Cell limits are deliberately modest. A workbook here is something a team
 * edits together in a chat sidebar, not a data warehouse: 20k cells per tab
 * keeps a full sheet inside one socket payload and one Mongo document.
 */
export const SHEET_MAX_ROWS = 2000;
export const SHEET_MAX_COLS = 64;
export const SHEET_MAX_CELLS_PER_TAB = 20000;
export const SHEET_MAX_TABS = 16;
export const sheetCellFormatSchema = z.object({
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    align: z.enum(['left', 'center', 'right']).optional(),
    decimals: z.number().int().min(0).max(10).optional(),
    bg: z.string().max(24).optional(),
    color: z.string().max(24).optional(),
});
export const sheetCellSchema = z.object({
    r: z
        .number()
        .int()
        .min(0)
        .max(SHEET_MAX_ROWS - 1),
    c: z
        .number()
        .int()
        .min(0)
        .max(SHEET_MAX_COLS - 1),
    input: z.string().max(4000),
    v: z.union([z.string().max(4000), z.number(), z.null()]).optional(),
    error: z.string().max(120).optional(),
    format: sheetCellFormatSchema.optional(),
    updatedBy: z.string(),
    updatedAt: z.number(),
});
/** A blank workbook, or one imported from a file already in the workspace. */
export const createSheetSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    /** Import: the uploaded .xlsx/.xls/.csv to read the first version from. */
    fileId: objectId.optional(),
})
    .refine((body) => Boolean(body.name || body.fileId), {
    message: 'Give the sheet a name, or a file to import',
    path: ['name'],
});
export const updateSheetSchema = z.object({
    name: z.string().trim().min(1).max(120),
});
export const createSlidesSchema = z
    .object({
    title: z.string().trim().min(1).max(160).optional(),
    /** Import: the uploaded `.pptx` to read the first version from. */
    fileId: objectId.optional(),
})
    .refine((body) => Boolean(body.title || body.fileId), {
    message: 'Give the deck a title, or a file to import',
    path: ['title'],
});
export const updateSlidesSchema = z.object({
    title: z.string().trim().min(1).max(160),
});
/**
 * Making a document, a workbook or a deck from nothing.
 *
 * Who it is for is decided here rather than afterwards: a file that is
 * workspace-wide for the first few minutes of its life has already been seen
 * by the time anyone thinks to narrow it, which is exactly the mistake this
 * is meant to prevent.
 */
export const createLibraryFileSchema = z
    .object({
    kind: z.enum(LIBRARY_FILE_KINDS),
    name: z.string().trim().min(1).max(160),
    visibility: z.enum(FILE_VISIBILITIES).default('workspace'),
    sharedWith: z.array(objectId).max(50).optional(),
    categoryId: objectId.optional(),
    spaceId: objectId.optional(),
    zoneId: z.string().optional(),
})
    .refine((body) => body.visibility !== 'shared' || (body.sharedWith?.length ?? 0) > 0, {
    message: 'Choose at least one person to share with',
    path: ['sharedWith'],
});
/**
 * Writing the first draft of a document, a workbook or a deck with the
 * assistant.
 *
 * The same audience fields as {@link createLibraryFileSchema}, for the same
 * reason: a draft nobody asked for is still a file, and who it is for has to
 * be settled before it exists rather than after. The brief is what the
 * assistant is given; `projectId` hands it the board as well, so "a status
 * report for this project" can name the work that is actually on it.
 *
 * The name is optional here, unlike a blank file: a person who has described
 * what they want has usually not also decided what to call it, so the
 * assistant titles it and they rename it if they disagree.
 */
export const generateLibraryFileSchema = z
    .object({
    kind: z.enum(LIBRARY_FILE_KINDS),
    brief: z.string().trim().min(4).max(GENERATED_FILE_BRIEF_MAX),
    name: z.string().trim().min(1).max(160).optional(),
    /** Draft it about the work on this project board. */
    projectId: objectId.optional(),
    /** How much to write. The assistant is otherwise inclined to write a lot. */
    length: z.enum(GENERATED_FILE_LENGTHS).default('standard'),
    visibility: z.enum(FILE_VISIBILITIES).default('workspace'),
    sharedWith: z.array(objectId).max(50).optional(),
    categoryId: objectId.optional(),
    spaceId: objectId.optional(),
    zoneId: z.string().optional(),
})
    .refine((body) => body.visibility !== 'shared' || (body.sharedWith?.length ?? 0) > 0, {
    message: 'Choose at least one person to share with',
    path: ['sharedWith'],
});
export const createSheetTabSchema = z.object({
    name: z.string().trim().min(1).max(60),
});
/**
 * Changing a tab: its name, or how far the grid reaches.
 *
 * Extent is here rather than implied by typing, because the client clamps
 * movement to the stored `rows`/`cols` — so without a way to ask for more,
 * the only rows that exist are the hundred a tab was made with. Never
 * shrinks: cells outside the new extent would still be stored and would
 * simply stop being visible, which is a worse outcome than a wide grid.
 */
export const updateSheetTabSchema = z
    .object({
    name: z.string().trim().min(1).max(60).optional(),
    rows: z.number().int().min(1).max(SHEET_MAX_ROWS).optional(),
    cols: z.number().int().min(1).max(SHEET_MAX_COLS).optional(),
})
    .refine((body) => body.name !== undefined || body.rows !== undefined || body.cols !== undefined, {
    message: 'Nothing to change',
});
export const sheetQuerySchema = z.object({
    fileId: objectId.optional(),
    mine: z
        .union([z.literal('1'), z.literal('true'), z.literal('0'), z.literal('false')])
        .optional(),
});
/* ------------------------------- documents ------------------------------ */
/** A paragraph is generous but not unbounded; a document is not a filesystem. */
export const DOC_MAX_BLOCKS = 5000;
export const DOC_MAX_BLOCK_LENGTH = 8000;
export const DOC_BLOCK_STYLES = [
    'p',
    'h1',
    'h2',
    'h3',
    'quote',
    'code',
    'bullet',
    'number',
    'todo',
    'divider',
];
export const docBlockSchema = z.object({
    id: z.string().min(1).max(64),
    text: z.string().max(DOC_MAX_BLOCK_LENGTH),
    style: z.enum(DOC_BLOCK_STYLES),
    checked: z.boolean().optional(),
    order: z.number().finite(),
    updatedBy: z.string(),
    updatedAt: z.number(),
});
/** A blank document, or one imported from a file already in the workspace. */
export const createDocSchema = z
    .object({
    title: z.string().trim().min(1).max(160).optional(),
    fileId: objectId.optional(),
})
    .refine((body) => Boolean(body.title || body.fileId), {
    message: 'Give the document a title, or a file to import',
    path: ['title'],
});
export const updateDocSchema = z.object({
    title: z.string().trim().min(1).max(160),
});
export const docQuerySchema = z.object({
    fileId: objectId.optional(),
    meetingId: objectId.optional(),
    mine: z
        .union([z.literal('1'), z.literal('true'), z.literal('0'), z.literal('false')])
        .optional(),
});
/* ----------------------------- file comments ---------------------------- */
export const createFileCommentSchema = z.object({
    body: z.string().trim().min(1).max(4000),
    /** Set to reply to an existing comment. */
    parentId: objectId.optional(),
});
export const updateFileCommentSchema = z.object({
    body: z.string().trim().min(1).max(4000),
});
/* -------------------------------- games -------------------------------- */
export const createGameSchema = z.object({
    kind: z.enum(GAME_KINDS),
    name: z.string().trim().min(1).max(80).optional(),
    spaceId: objectId.optional(),
    zoneId: z.string().max(60).optional(),
    /** Invite someone to the opposite seat; otherwise the table stays open. */
    opponentId: objectId.optional(),
});
export const gameMoveSchema = z.object({
    from: z.number().int().min(0).max(255).optional(),
    to: z.number().int().min(0).max(255),
    promotion: z.enum(['Q', 'R', 'B', 'N']).optional(),
    die: z.number().int().min(1).max(6).optional(),
});
/* ------------------------------ bookings ------------------------------- */
/**
 * Holding a desk or a room.
 *
 * The resource is named as one of two fields rather than as a kind plus an id,
 * so a request that says "room" while pointing at a desk cannot be written
 * down. Everything else about the window — not in the past, not longer than a
 * day, not beyond the workspace's horizon — is checked on the server, where
 * the workspace's own settings and the floor plan are.
 */
export const createBookingSchema = z
    .object({
    spaceId: objectId,
    zoneId: z.string().trim().min(1).max(60).optional(),
    furnitureId: z.string().trim().min(1).max(60).optional(),
    title: z.string().trim().min(1).max(120).optional(),
    start: z.string().datetime(),
    end: z.string().datetime(),
    /** Opens this meeting when the booker walks into the room. */
    meetingId: objectId.optional(),
    /**
     * Book the same window on every remaining working day of this week. One
     * booking per day is still written — a repeat is a convenience at the
     * keyboard, not a thing that can be cancelled as a unit.
     */
    repeatThisWeek: z.boolean().optional(),
})
    .refine((body) => Boolean(body.zoneId) !== Boolean(body.furnitureId), 'Book either a room or a desk');
/** Moving a booking, or retitling it. The resource itself never changes. */
export const updateBookingSchema = z
    .object({
    start: z.string().datetime().optional(),
    end: z.string().datetime().optional(),
    title: z.string().trim().min(1).max(120).nullable().optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'Nothing to update');
/* ------------------------------ meetings ------------------------------- */
export const createMeetingSchema = z.object({
    title: z.string().trim().min(1).max(120),
    description: z.string().max(2000).optional(),
    spaceId: objectId.optional(),
    zoneId: z.string().optional(),
    inviteeIds: z.array(objectId).max(500).optional(),
    cohostIds: z.array(objectId).max(20).optional(),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    recurrence: z.enum(['none', 'daily', 'weekly']).default('none'),
    category: z.enum(MEETING_CATEGORIES).default('work'),
    attachmentIds: z.array(objectId).max(50).optional(),
    settings: z
        .object({
        muteOnEntry: z.boolean().default(false),
        locked: z.boolean().default(false),
        requireApproval: z.boolean().default(false),
        allowScreenShare: z.boolean().default(true),
        allowChat: z.boolean().default(true),
    })
        .partial()
        .optional(),
});
export const updateMeetingSchema = createMeetingSchema.partial();
// The optional fields keep older cached clients compatible during rollout.
// The server deliberately ignores them: guests enter their own details.
export const createMeetingGuestInviteSchema = z.object({
    name: z.string().trim().min(2).max(80).optional(),
    email: z.string().trim().email().max(254).optional(),
});
export const joinMeetingGuestSchema = z.object({
    name: z.string().trim().min(2).max(80),
    email: z.string().trim().email().max(254).optional(),
});
export const validateMeetingGuestSessionSchema = z.object({
    guestToken: z.string().min(20).max(4000),
    meetingId: objectId,
});
/* ------------------------------- misc ---------------------------------- */
export const pushTokenSchema = z.object({
    token: z.string().min(10).max(300),
    platform: z.enum(['ios', 'android', 'web']),
});
export const searchSchema = z.object({
    q: z.string().trim().min(1).max(200),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
/* --------------------- Itsappy AI assistant ---------------------------- */
/**
 * The brief someone types before asking for suggestions. Long enough to
 * describe a product, capped low enough that nobody pastes a whole spec in —
 * the ceiling is what stops one request becoming an unbounded prompt.
 */
const assistantBrief = z.string().trim().min(10).max(2000);
export const suggestNamesSchema = z.object({
    subject: z.enum(ASSISTANT_SUBJECTS),
    brief: assistantBrief,
    count: z.coerce.number().int().min(1).max(10).default(6),
    /** Names already taken or already turned down, so a retry moves on. */
    avoid: z.array(z.string().trim().min(1).max(80)).max(50).default([]),
});
export const suggestFeaturesSchema = z.object({
    brief: assistantBrief,
    subject: z.enum(ASSISTANT_SUBJECTS).default('project'),
    count: z.coerce.number().int().min(1).max(12).default(8),
    /** What is already on the board, so suggestions extend instead of repeat. */
    existing: z.array(z.string().trim().min(1).max(200)).max(100).default([]),
});
export const assistantAskSchema = z.object({
    prompt: z.string().trim().min(1).max(4000),
    /** Where the question was asked from, so the answer can be grounded. */
    context: z.string().trim().max(4000).optional(),
    /**
     * A conversation to read before answering, named rather than pasted: the
     * server fetches the window itself, so "the last month" works even when the
     * client has only paged in this morning's messages. Access is checked
     * against the same rule the channel itself uses.
     */
    chat: z
        .object({
        channelId: objectId,
        range: z.enum(CHAT_CONTEXT_RANGES),
    })
        .optional(),
});
/**
 * What a client reports back after running — or waving away — a proposal.
 *
 * The client executes actions through the ordinary APIs so permissions,
 * notifications and realtime all behave exactly as they do for a hand-made
 * task; this only records the outcome on the turn that proposed it.
 */
export const assistantActionResultSchema = z.object({
    state: z.enum(['done', 'dismissed']),
    label: z.string().trim().max(120).optional(),
    link: z.string().trim().max(500).optional(),
});
export const createSpaceAgentSchema = z.object({
    name: z.string().trim().min(2).max(60),
    role: z.enum(SPACE_AGENT_ROLES),
    instructions: z.string().trim().max(2000).optional(),
    zoneId: z.string().trim().max(64).optional(),
    modelSlug: z.string().trim().max(120).optional(),
    enabled: z.boolean().default(true),
    /* Opt-in: an agent that speaks to you unbidden is a different thing from one
     * that waits to be asked, and only the room's own people can say which they
     * want standing there. */
    greets: z.boolean().default(false),
});
export const updateSpaceAgentSchema = createSpaceAgentSchema.partial();
export const walkSpaceAgentSchema = z
    .object({
    zoneId: z.string().min(1).optional(),
    targetUserId: z.string().min(1).optional(),
    targetPos: z.object({ x: z.number(), y: z.number() }).optional(),
    follow: z.boolean().optional(),
})
    .refine((data) => data.zoneId !== undefined ||
    data.targetUserId !== undefined ||
    data.targetPos !== undefined ||
    data.follow !== undefined, { message: 'Provide a zoneId, targetUserId, targetPos, or follow flag' });
/* ----------------------------- project agents ---------------------------- */
const projectAgentSource = z.object({
    kind: z.enum(PROJECT_AGENT_SOURCE_KINDS),
    id: objectId,
    title: z.string().trim().min(1).max(200),
});
export const createProjectAgentSchema = z.object({
    name: z.string().trim().min(2).max(60),
    brief: z.string().trim().max(4000).optional(),
    sources: z.array(projectAgentSource).max(PROJECT_AGENT_MAX_SOURCES).default([]),
    enabled: z.boolean().default(true),
    /* Opt-in, and only ever by somebody who can manage the project: an agent
       that files tasks unasked is a different proposition from one that suggests
       them, and that choice belongs to the board's own people. */
    autoCreate: z.boolean().default(false),
    modelSlug: z.string().trim().max(120).optional(),
});
export const updateProjectAgentSchema = createProjectAgentSchema.partial();
/**
 * Asking the agent to plan without a task having closed — a change request, a
 * new demand, a requirement somebody just agreed to.
 */
export const projectAgentRequestSchema = z.object({
    request: z.string().trim().min(3).max(4000),
});
/**
 * Which of a review's suggestions to keep.
 *
 * An absent list means all of them, which is what the "Accept all" button
 * sends; an empty list is refused rather than treated as all, because the two
 * readings of `[]` differ by six tasks appearing on a board.
 */
export const decideAgentReviewSchema = z.object({
    followUpIds: z
        .array(z.string().trim().min(1).max(40))
        .min(1)
        .max(PROJECT_AGENT_MAX_FOLLOW_UPS)
        .optional(),
    assigneeIds: z.array(objectId).max(20).optional(),
    priority: z.enum(TASK_PRIORITIES).optional(),
    type: z.enum(TASK_TYPES).optional(),
});
export const runSpaceAgentSchema = z.object({
    task: z.string().trim().min(1).max(4000),
    context: z.string().trim().max(4000).optional(),
});
/* ----------------------------- Waitlist ------------------------------ */
/**
 * Ask to be told when the Android app lands.
 *
 * Deliberately one field. Everything else a marketing form usually asks for —
 * team size, company, a role dropdown — is information nobody will act on
 * before the build exists, and each box costs a share of the people who would
 * otherwise have finished.
 */
export const joinWaitlistSchema = z.object({
    email: z.string().trim().toLowerCase().email().max(180),
    platform: z.enum(WAITLIST_PLATFORMS).default('android'),
    /**
     * Where the address came from, so a campaign can be told apart from the
     * landing page later. Client-supplied and therefore never trusted for
     * anything but counting.
     */
    source: z.string().trim().max(60).optional(),
});
/* ----------------------------- api keys -------------------------------- */
export const createApiKeySchema = z.object({
    name: z.string().trim().min(1).max(80),
    scopes: z.array(z.enum(API_KEY_SCOPES)).min(1),
    /** Absent means the key does not expire on its own; revoking is then the only way out. */
    expiresAt: z.string().datetime().optional(),
});
/* ------------------------------ webhooks -------------------------------- */
export const createIncomingWebhookSchema = z.object({
    name: z.string().trim().min(1).max(MAX_WEBHOOK_NAME_LENGTH),
    channelId: z.string().min(1),
});
/**
 * Only http(s), and the host is checked again server-side against the same
 * private-address rules link previews use — a schema cannot resolve DNS.
 */
const webhookUrl = z
    .string()
    .trim()
    .url()
    .max(2000)
    .refine((value) => /^https?:\/\//i.test(value), 'Only http and https URLs can be called');
export const createOutgoingWebhookSchema = z.object({
    url: webhookUrl,
    events: z.array(z.enum(OUTGOING_WEBHOOK_EVENTS)).min(1),
    enabled: z.boolean().optional(),
});
export const updateOutgoingWebhookSchema = z
    .object({
    url: webhookUrl,
    events: z.array(z.enum(OUTGOING_WEBHOOK_EVENTS)).min(1),
    enabled: z.boolean(),
})
    .partial();
const incomingAttachmentSchema = z
    .object({
    title: z.string().max(300).optional(),
    text: z.string().max(MAX_MESSAGE_LENGTH).optional(),
    /* Slack calls these `title_link` / `fallback`; accepted so a preset
       built for Slack posts something readable rather than an empty card. */
    url: z.string().max(2000).optional(),
    title_link: z.string().max(2000).optional(),
    fallback: z.string().max(MAX_MESSAGE_LENGTH).optional(),
    color: z.string().max(20).optional(),
})
    .passthrough();
/**
 * Lenient on purpose: this is what third parties post, and every field they
 * might leave out or add is tolerated. The one hard rule — that there is
 * something to say — is checked in the route after blocks are flattened.
 */
export const incomingWebhookPayloadSchema = z
    .object({
    text: z.string().max(MAX_MESSAGE_LENGTH).optional(),
    username: z.string().trim().max(MAX_WEBHOOK_NAME_LENGTH).optional(),
    iconEmoji: z.string().trim().max(64).optional(),
    icon_emoji: z.string().trim().max(64).optional(),
    attachments: z
        .array(incomingAttachmentSchema)
        .max(MAX_INCOMING_WEBHOOK_ATTACHMENTS)
        .optional(),
    blocks: z.array(z.unknown()).max(50).optional(),
})
    .passthrough();
